import React from 'react'
import { useTranslation } from 'react-i18next'
import {Badge, Form} from 'antd'
import {observer} from 'mobx-react'

import {useScreenType} from '@/hooks'
import {LoadingState} from '@/types'
import {PASSWORD_PATTERN} from '@/constants/patterns'
import {withTitle} from '@/router/utils'
import {BackToLoginLink, Button, Card, InputPassword, Popover} from '@/components'
import {
    CONFIRM_NEW_PASSWORD_INPUT,
    SET_NEW_PASSWORD_BUTTON,
    SET_NEW_PASSWORD_INPUT
} from '@/constants/playwright-ids'

import { SetPasswordProps } from './props'
import styles from './styles.scss'

const SetPassword = observer(({store, formId}: SetPasswordProps) => {
    const { t } = useTranslation()
    const [form] = Form.useForm()
    const {isMobile} = useScreenType()

    const onFinish = () => {
        store.changePassword()
    }

    const popoverContent = () => {
        return (
            <div>
                {Object.entries(t('SetPassword.codeRestrictions')).map(([key, value]) => (
                    <div key={key}>
                        {store.restrictionErrors[key] ? (
                            <><Badge status={'error'} /> {value}</>
                        ) : (
                            <><Badge status={'success'} /> {value}</>
                        )}
                    </div>
                ))}
            </div>
        )
    }

    return (
        <>
            <div className={styles.setPasswordModalContainer}>
                <BackToLoginLink />

                <Card title={t('SetPassword.title')}>
                    <Form
                        form={form}
                        id={formId}
                        layout={'vertical'}
                        requiredMark={false}
                        onFinish={onFinish}
                    >
                        <Popover
                            content={popoverContent()}
                            title={t('SetPassword.codeMustContain')}
                            placement={isMobile ? 'bottom' : 'right'}
                        >
                            <Form.Item
                                label={t('SetPassword.newPassword')}
                                name={'password'}
                                rules={[
                                    {
                                        required: true,
                                        message: t('SetPassword.errors.newPasswordRequired')
                                    },
                                    {
                                        pattern: PASSWORD_PATTERN,
                                        message: t('SetPassword.errors.newPasswordInvalid')
                                    }
                                ]}
                            >
                                <InputPassword
                                    onChange={(e) => store.setPassword(e.target.value)}
                                    placeholder={t('SetPassword.newPassword')}
                                    data-test-id={SET_NEW_PASSWORD_INPUT}
                                />
                            </Form.Item>
                        </Popover>
                        <Form.Item
                            label={t('SetPassword.confirmPassword')}
                            name={'confirm'}
                            rules={[
                                {
                                    required: true,
                                    message: t('SetPassword.errors.confirmPasswordRequired')
                                },
                                {
                                    validator: (field, value) => {
                                        if (value === store.password) {
                                            return Promise.resolve()
                                        }

                                        return Promise.reject(
                                            new Error(t('SetPassword.errors.confirmPasswordInvalid'))
                                        )
                                    }
                                }
                            ]}
                        >
                            <InputPassword
                                placeholder={t('SetPassword.confirmPassword')}
                                data-test-id={CONFIRM_NEW_PASSWORD_INPUT}
                            />
                        </Form.Item>
                        <Form.Item key={'submit'}>
                            <Button
                                form={formId}
                                type={'primary'}
                                htmlType={'submit'}
                                className={styles.submit}
                                loading={
                                    store.passwordChangingLoadingState === LoadingState.LOADING
                                }
                                data-test-id={SET_NEW_PASSWORD_BUTTON}
                            >
                                {t('SetPassword.submit')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </>
    )
})

export default withTitle(SetPassword)
